// The dashboard's brand-new-tenant panel (no runners, no deployments, no runs): instead of
// a strip of zeros, it sells the runner model and points at Register a runner.
//
// Every claim here is load-bearing — keep it true to the product, not aspirational:
//   * where it runs   — the customer deploys the runner into their own Azure subscription,
//                       resource group and region (infra/runner-sample, runner-marketplace);
//   * secrets         — vendor credentials live in the runner's private-endpoint Key Vault and
//                       are never stored by the control plane; the runner's connection to the
//                       control plane is outbound-only HTTPS, each call HMAC-SHA256 signed, and
//                       CloudRadial keeps only a hash of its secret (calls to AI providers use the
//                       provider's own API-key or identity auth, not that signature);
//   * AI cost         — runner AI (agents and script steps) calls the customer's own provider
//                       account and is never billed as Usage Credits (AAI-124); AutomationAI's own
//                       AI (AI Generate, planning, run summaries) does use Usage Credits, so the
//                       copy scopes the "no markup" promise to the runner.
// Help-center links are the same articles the Getting Started checklist uses.

import { useNavigate } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { lockIcon, mapMarkerTargetIcon, walletIcon, type SVGIcon } from '@progress/kendo-svg-icons';
import './RunnerOnboarding.css';

const KB = 'https://support.cloudradial.com/hc/en-us/articles';

const BENEFITS: { title: string; body: string; icon: SVGIcon; more: string; href: string }[] = [
  {
    title: 'You choose where it runs',
    body:
      'Deploy the runner into your own Azure subscription, in the region and resource group you pick, next to the systems it automates. You own the Function Apps, so you decide what it can reach.',
    icon: mapMarkerTargetIcon,
    more: 'Deploying a runner',
    href: `${KB}/52548738158100-Deploying-a-runner-from-the-Azure-Marketplace`,
  },
  {
    title: 'Secrets stay in your tenant',
    body:
      "Vendor credentials live in the runner's private Key Vault, and CloudRadial never stores them. The runner reaches AutomationAI over outbound HTTPS with every call signed, so there's no inbound port for anyone to reach.",
    icon: lockIcon,
    more: 'Your runner’s Key Vault',
    href: `${KB}/50683016852884-Accessing-and-updating-your-runner-s-Key-Vault`,
  },
  {
    title: 'AI at your provider’s price',
    body:
      'The AI your runners use goes to your own OpenAI, Anthropic or Azure AI Foundry account, billed directly by the provider with no markup from us. Building with AI Generate and Orion’s planning uses your plan’s Usage Credits.',
    icon: walletIcon,
    more: 'Choosing the AI provider',
    href: `${KB}/50681908806676-Choosing-the-runner-AI-provider`,
  },
];

const FACTS = ['outbound HTTPS only', 'HMAC-SHA256 signed check-ins', 'private-endpoint Key Vault', 'self-updating'];

export function RunnerOnboarding() {
  const navigate = useNavigate();
  return (
    <section className="ro" aria-labelledby="ro-heading">
      <p className="ro-eyebrow">First step · connect a runner</p>
      <h2 id="ro-heading" className="ro-heading">
        Your automations run in your Azure, not ours.
      </h2>
      <p className="ro-lede">
        AutomationAI plans and orchestrates. A runner, a small set of Azure Functions you deploy, does the work inside your
        environment. You don’t hand a SaaS platform your vendor API keys, and the AI your runners use is billed by your own
        provider, not marked up by us.
      </p>

      <ul className="ro-benefits">
        {BENEFITS.map((b) => (
          <li key={b.title} className="ro-benefit">
            <SvgIcon icon={b.icon} className="ro-benefit-icon" aria-hidden />
            <h3 className="ro-benefit-title">{b.title}</h3>
            <p className="ro-benefit-body">{b.body}</p>
            <a className="ro-benefit-link" href={b.href} target="_blank" rel="noopener noreferrer">
              {b.more}
              <span className="ro-sr-only"> (opens in a new tab)</span>
            </a>
          </li>
        ))}
      </ul>

      <ul className="ro-facts" aria-label="How the runner connects">
        {FACTS.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>

      <div className="ro-actions">
        <Button themeColor="primary" onClick={() => navigate('/runners')}>
          Register a runner
        </Button>
        <Button fillMode="flat" onClick={() => navigate('/designer')}>
          Create a workflow
        </Button>
      </div>
    </section>
  );
}

export default RunnerOnboarding;
